import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CREATE, DataAPIContext, GET_ONE } from '../api';
import { isOk, serverErrorMessage } from '../utils/errors';
import { editableTeamRule, parseAddressList, sameAddressList } from './webWidgetContact';

const RULE_EVENT_LABEL = "'Website visitor left contact details for the team'";

/**
 * "Send team emails to" under the Assistant tab's "Ask for the visitor's email" switch
 * (backend: GET/POST web-chat/contact-team/, demo/lib/web_widget_contact.py).
 *
 * The team email is a Notification rule. With no rule, saving here creates one with the
 * default subject/body; with one plain rule of this operator's own, the field edits its
 * addresses in place and "Customize the email" opens it in Notifications. Anything else (a
 * franchise parent's rule, conditions, several rules) is listed read-only with a link.
 * Saves on its own button: it writes a rule, not the operator config the page's Save sends.
 *
 * Renders the old one-line hint when the backend can't answer (older backend, no access).
 */
const WebWidgetContactTeam = ({ operatorCode }) => {
  const dataAPI = React.useContext(DataAPIContext);
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = React.useState(null);
  const [text, setText] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const applyStatus = React.useCallback((data) => {
    setStatus(data);
    const rule = editableTeamRule(data);
    setText(rule ? rule.destinations.join(', ') : '');
    setError('');
  }, []);

  React.useEffect(() => {
    if (!dataAPI || !operatorCode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await dataAPI(GET_ONE, 'web-chat', {
          urlSuffix: 'contact-team/',
          operator_id: operatorCode,
          keepSessionOn403: true,
        });
        if (!cancelled) applyStatus(isOk(res) && res.data && res.data.known ? res.data : null);
      } catch (e) {
        if (!cancelled) setStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [dataAPI, operatorCode, applyStatus]);

  if (!status) {
    return (
      <Typography variant="caption" sx={{ display: 'block', ml: 6, mt: 1 }} data-testid="widget-contact-team-fallback">
        Set up who receives it under Notifications → {RULE_EVENT_LABEL}.
      </Typography>
    );
  }

  const editable = editableTeamRule(status);
  const inPlace = status.can_create || !!editable;
  const saved = editable ? editable.destinations : [];
  const typed = parseAddressList(text);
  const dirty = !sameAddressList(typed, saved);

  const onSave = async () => {
    setSaving(true);
    try {
      const res = await dataAPI(CREATE, 'web-chat', {
        urlSuffix: 'contact-team/',
        data: { operator_id: operatorCode, addresses: typed },
        keepSessionOn403: true,
      });
      if (isOk(res) && res.data && res.data.known) {
        applyStatus(res.data);
        enqueueSnackbar('Team email addresses saved', { variant: 'success' });
      } else {
        setError(serverErrorMessage(res, 'Could not save the addresses'));
      }
    } catch (e) {
      setError('Could not save the addresses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ ml: 6, mt: 2, maxWidth: 640 }} data-testid="widget-contact-team">
      {!status.emails_to_address_allowed && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Emails to fixed addresses are switched off for your account, so your team won&apos;t
          get these emails. Contact TourOptima support to turn them on.
        </Alert>
      )}

      {inPlace ? (
        <>
          {status.can_create && (
            <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="widget-contact-team-none">
              Nobody gets these emails yet. Add your team&apos;s address so visitors who leave
              their email get an answer.
            </Alert>
          )}
          <TextField
            id="widget-contact-team-addresses"
            disabled={!status.can_edit || saving}
            variant="filled"
            size="small"
            fullWidth
            label="Send team emails to"
            placeholder="team@yourcompany.com"
            InputLabelProps={{ shrink: true }}
            value={text}
            onChange={(e) => { setText(e.target.value); setError(''); }}
            error={!!error}
            helperText={error || (status.can_edit
              ? 'Separate several addresses with commas. A shared team inbox works best.'
              : 'You need permission to edit notifications to change this.')}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={!status.can_edit || saving || !dirty || typed.length === 0}
              onClick={onSave}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Save addresses
            </Button>
            {editable && (
              <Link component={RouterLink} to={`/notifications/${editable.id}`} variant="body2">
                Customize the email
              </Link>
            )}
          </Box>
        </>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Team emails go to</Typography>
          {status.rules.map((rule) => (
            <Box key={rule.id} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                {rule.destinations.length ? rule.destinations.join(', ') : 'Nobody (no delivery method is on)'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rule.inherited ? 'Set by your parent account in Notifications: ' : 'From the notification '}
                {rule.inherited ? rule.name : (
                  <Link component={RouterLink} to={`/notifications/${rule.id}`}>{rule.name}</Link>
                )}
                {rule.has_conditions ? ' (only for some conversations)' : ''}
              </Typography>
            </Box>
          ))}
          {!status.has_recipients && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Nobody gets these emails. Fix it in Notifications, under {RULE_EVENT_LABEL}.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

WebWidgetContactTeam.propTypes = {
  operatorCode: PropTypes.string,
};

export default WebWidgetContactTeam;
